<!-- src/apps/secret/components/delivery/RecipientLinksList.vue -->

<script setup lang="ts">
  /**
   * Eightpoint: delivery details on the sender's receipt page.
   *
   * - views used/remaining for multi-view links
   * - a note when recipients must verify their email address
   * - for a send to several recipients, each recipient's own link and its
   *   status (not opened / opened / viewed / burned / expired)
   *
   * Renders nothing for receipts that use none of these options.
   */
  import { useI18n } from 'vue-i18n';
  import type { RecipientLink, RecipientLinkStatus } from '@/schemas/shapes/v3/delivery-options';
  import { computed } from 'vue';

  interface ReceiptDeliveryRecord {
    max_views?: number;
    views_used?: number;
    views_remaining?: number;
    verify_recipient?: boolean;
    bundle?: RecipientLink[];
    identifier?: string;
  }

  interface Props {
    record: ReceiptDeliveryRecord;
  }

  const props = defineProps<Props>();
  const { t } = useI18n();

  const multiView = computed(() => (props.record.max_views ?? 1) > 1);
  const links = computed(() => props.record.bundle ?? []);
  const visible = computed(
    () => multiView.value || !!props.record.verify_recipient || links.value.length > 0
  );

  const statusLabel = (status: RecipientLinkStatus) => t(`web.secrets.delivery.status_${status}`);

  const statusClass = (status: RecipientLinkStatus) =>
    ({
      pending: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-200',
      opened: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200',
      revealed: 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-200',
      burned: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200',
      expired: 'bg-gray-200 text-gray-600 dark:bg-gray-700 dark:text-gray-300',
      orphaned: 'bg-gray-200 text-gray-600 dark:bg-gray-700 dark:text-gray-300',
    })[status];
</script>

<template>
  <section
    v-if="visible"
    class="border-t border-gray-200/60 p-4 sm:p-6 dark:border-gray-700/40"
    aria-labelledby="delivery-details-heading"
    data-testid="receipt-delivery-details">
    <h2
      id="delivery-details-heading"
      class="sr-only">
      {{ t('web.secrets.delivery.receipt_heading') }}
    </h2>

    <p
      v-if="multiView && !links.length"
      class="text-sm text-gray-700 dark:text-gray-300"
      data-testid="receipt-views-summary">
      {{ t('web.secrets.delivery.receipt_views', { used: record.views_used ?? 0, max: record.max_views }) }}
    </p>

    <p
      v-if="record.verify_recipient"
      class="mt-1 text-sm text-gray-600 dark:text-gray-400">
      {{ t('web.secrets.delivery.receipt_verification_on') }}
    </p>

    <div
      v-if="links.length"
      class="mt-2">
      <h3 class="text-base font-semibold text-gray-900 dark:text-white">
        {{ t('web.secrets.delivery.receipt_recipients_title', { count: links.length }) }}
      </h3>
      <p class="mt-1 text-sm text-gray-600 dark:text-gray-400">
        {{ t('web.secrets.delivery.receipt_recipients_hint') }}
      </p>
      <ul
        class="mt-3 divide-y divide-gray-200/60 dark:divide-gray-700/50"
        data-testid="receipt-recipient-links">
        <li
          v-for="link in links"
          :key="link.identifier"
          class="flex flex-wrap items-center justify-between gap-2 py-2"
          data-testid="receipt-recipient-link">
          <span class="min-w-0 break-all text-sm text-gray-800 dark:text-gray-200">
            {{ link.recipient || t('web.secrets.delivery.unknown_recipient') }}
          </span>
          <span class="flex items-center gap-3">
            <span
              v-if="link.max_views > 1"
              class="text-xs text-gray-500 dark:text-gray-400">
              {{ t('web.secrets.delivery.views_short', { used: link.views_used, max: link.max_views }) }}
            </span>
            <span
              :class="statusClass(link.status)"
              class="rounded-full px-2 py-0.5 text-xs font-medium"
              data-testid="receipt-recipient-status">
              {{ statusLabel(link.status) }}
            </span>
            <RouterLink
              v-if="link.identifier !== record.identifier"
              :to="`/receipt/${link.identifier}`"
              class="text-xs font-medium text-brand-600 hover:text-brand-500 dark:text-brand-400">
              {{ t('web.secrets.delivery.receipt_link_details') }}
            </RouterLink>
          </span>
        </li>
      </ul>
    </div>
  </section>
</template>
